import type { DataStatus, ProtocolFilter } from "../types";

type Page = "dashboard" | "methodology";

const PROTOCOL_OPTIONS: { value: ProtocolFilter; label: string }[] = [
  { value: "all", label: "All Protocols" },
  { value: "aave_v3", label: "Aave V3" },
  { value: "compound_v3", label: "Compound V3" },
  { value: "morpho_blue", label: "Morpho Blue" },
];

interface HeaderProps {
  status: DataStatus;
  lastFetched: Date | null;
  dataTimestamp: string | null;
  protocolFilter: ProtocolFilter;
  onProtocolChange: (filter: ProtocolFilter) => void;
  activeProtocols: string[];
  page: Page;
  onPageChange: (page: Page) => void;
}

export function Header({
  status,
  dataTimestamp,
  protocolFilter,
  onProtocolChange,
  activeProtocols,
  page,
  onPageChange,
}: HeaderProps) {
  const statusDotClass =
    status === "fresh" ? "status-dot-fresh" :
    status === "stale" ? "status-dot-stale" :
    status === "error" ? "status-dot-error" :
    "status-dot bg-surface-600";

  const timeAgo = dataTimestamp
    ? formatTimeAgo(new Date(dataTimestamp))
    : "—";

  return (
    <header className="sticky top-0 z-50 bg-surface-900/95 backdrop-blur-sm border-b border-surface-600">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
        {/* Left: title + chain badge + nav */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => { onPageChange("dashboard"); window.scrollTo(0, 0); }}
            className="text-lg font-semibold tracking-tight hover:text-accent transition-colors"
          >
            DeFi Risk Dashboard
          </button>
          <span className="text-xs font-mono bg-surface-700 text-text-secondary px-2 py-0.5 rounded">
            Ethereum
          </span>
          <span className="text-surface-600">|</span>
          <button
            onClick={() => { onPageChange("methodology"); window.scrollTo(0, 0); }}
            className={`text-xs font-medium transition-colors ${
              page === "methodology"
                ? "text-accent"
                : "text-text-muted hover:text-text-primary"
            }`}
          >
            Methodology
          </button>
        </div>

        {/* Center: protocol selector (only on dashboard) */}
        {page === "dashboard" && (
          <div className="hidden sm:flex items-center gap-1 bg-surface-800 rounded-lg p-0.5">
            {PROTOCOL_OPTIONS.map(({ value, label }) => {
              const isActive = protocolFilter === value;
              const isAvailable = value === "all" || activeProtocols.includes(value);
              return (
                <button
                  key={value}
                  onClick={() => onProtocolChange(value)}
                  disabled={!isAvailable}
                  className={`
                    px-3 py-1.5 text-xs font-medium rounded-md transition-colors
                    ${isActive
                      ? "bg-accent text-white"
                      : isAvailable
                        ? "text-text-secondary hover:text-text-primary hover:bg-surface-700"
                        : "text-text-muted opacity-40 cursor-not-allowed"
                    }
                  `}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {/* Mobile: protocol dropdown */}
        {page === "dashboard" && (
          <select
            value={protocolFilter}
            onChange={(e) => onProtocolChange(e.target.value as any)}
            className="sm:hidden bg-surface-800 text-text-secondary text-xs rounded-md px-2 py-1.5 border border-surface-600"
          >
            {PROTOCOL_OPTIONS.map(({ value, label }) => {
              const isAvailable = value === "all" || activeProtocols.includes(value);
              return (
                <option key={value} value={value} disabled={!isAvailable}>
                  {label}
                </option>
              );
            })}
          </select>
        )}

        {/* Right: freshness indicator */}
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <span className={statusDotClass} />
          <span className="font-mono">{timeAgo}</span>
        </div>
      </div>
    </header>
  );
}

function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 10) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}
